<template>
	<div class="app-custom-textarea-wrapper">
		<textarea
			ref="textareaRef"
			class="app-custom-textarea"
			:class="{ 'app-custom-textarea_disabled': disabled }"
			:value="modelValue"
			:placeholder="placeholder"
			:disabled="disabled"
			:style="textareaStyle"
			@input="handleInput"
			@keydown="handleKeydown"
			@focus="$emit('focus')"
			@blur="$emit('blur')"
		/>

		<app-tooltip
			v-if="showClearButton && hasValue && !disabled"
			class="app-custom-textarea__clear"
			:text="clearHintText"
		>
			<template #activator>
				<app-icon
					name="arrow-left"
					width="16px"
					color="#979797"
					cursor="pointer"
					@click="clearField"
					@mousedown.prevent
				/>
			</template>
		</app-tooltip>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from 'vue'
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue'
import AppTooltip from '@/components/ui-kit/tooltip/AppTooltip.vue'

type NewLineMode = 'enter' | 'ctrl-enter' | 'shift-enter' | 'none'

type AppCustomTextareaProps = {
	modelValue?: string
	placeholder?: string
	minHeight?: number
	maxHeight?: number
	disabled?: boolean
	autoFocus?: boolean
	newLineMode?: NewLineMode
	showClearButton?: boolean
	clearHintText?: string
}

type AppCustomTextareaEmits = {
	(e: 'update:modelValue', value: string): void
	(e: 'focus'): void
	(e: 'blur'): void
	(e: 'change', value: string): void
	(e: 'enter'): void
	(e: 'ctrl-enter'): void
	(e: 'shift-enter'): void
	(e: 'clear', value: string): void
}

const props = withDefaults(defineProps<AppCustomTextareaProps>(), {
	modelValue: '',
	placeholder: 'Введите текст...',
	minHeight: 32,
	maxHeight: 0,
	disabled: false,
	autoFocus: false,
	newLineMode: 'enter',
	showClearButton: true,
	clearHintText: 'Очистить поле'
})

const emit = defineEmits<AppCustomTextareaEmits>()

const textareaRef = ref<HTMLTextAreaElement | null>(null)

const hasValue = computed(() => props.modelValue.length > 0)

const textareaStyle = computed(() => {
	const styles: Record<string, string> = {
		minHeight: `${props.minHeight}px`
	}
	if (props.maxHeight) {
		styles.maxHeight = `${props.maxHeight}px`
	}
	// Освобождаем место справа под иконку очистки, чтобы текст не уходил под неё
	if (props.showClearButton && hasValue.value && !props.disabled) {
		styles.paddingRight = '32px'
	}
	return styles
})

const resize = () => {
	const el = textareaRef.value
	if (!el) return

	el.style.height = `${props.minHeight}px`
	const style = window.getComputedStyle(el)
	const borderY =
		parseFloat(style.borderTopWidth || '0') +
		parseFloat(style.borderBottomWidth || '0')
	el.style.height = `${Math.max(props.minHeight, el.scrollHeight + borderY)}px`
}

const handleInput = (event: Event) => {
	const value = (event.target as HTMLTextAreaElement).value
	emit('update:modelValue', value)
	emit('change', value)
	nextTick(resize)
}

const handleKeydown = (event: KeyboardEvent) => {
	if (event.key !== 'Enter') return
	const { ctrlKey, shiftKey, metaKey } = event

	if (props.newLineMode === 'enter') {
		if (ctrlKey || metaKey) {
			event.preventDefault()
			emit('ctrl-enter')
		} else if (shiftKey) {
			emit('shift-enter')
		}
	} else if (props.newLineMode === 'shift-enter') {
		if (shiftKey) {
			emit('shift-enter')
		} else if (ctrlKey || metaKey) {
			event.preventDefault()
			emit('ctrl-enter')
		} else {
			event.preventDefault()
			emit('enter')
		}
	} else if (props.newLineMode === 'ctrl-enter') {
		if (ctrlKey || metaKey) {
			emit('ctrl-enter')
		} else if (shiftKey) {
			event.preventDefault()
			emit('shift-enter')
		} else {
			event.preventDefault()
			emit('enter')
		}
	} else if (props.newLineMode === 'none') {
		if (ctrlKey || metaKey) {
			event.preventDefault()
			emit('ctrl-enter')
		} else if (shiftKey) {
			event.preventDefault()
			emit('shift-enter')
		} else {
			event.preventDefault()
			emit('enter')
		}
	}
}

const clearField = () => {
	if (props.disabled) return

	emit('update:modelValue', '')
	emit('change', '')
	emit('clear', '')
	nextTick(() => {
		resize()
		textareaRef.value?.focus()
	})
}

watch(() => props.modelValue, () => nextTick(resize))

onMounted(() => {
	resize()
	if (props.autoFocus && !props.disabled) {
		nextTick(() => textareaRef.value?.focus())
	}
})

defineExpose({
	focus: () => textareaRef.value?.focus(),
	blur: () => textareaRef.value?.blur(),
	reset: () => {
		if (props.modelValue !== '') {
			emit('update:modelValue', '')
			emit('change', '')
		}
		nextTick(resize)
	},
	getValue: () => textareaRef.value?.value ?? ''
})
</script>

<style scoped lang="scss">
.app-custom-textarea-wrapper {
	position: relative;
	width: 100%;
}

.app-custom-textarea__clear {
	position: absolute;
	top: 8px;
	right: 10px;
	z-index: 1;
}

.app-custom-textarea {
	width: 100%;
	box-sizing: border-box;
	padding: 6px 12px;
	font-size: 14px;
	line-height: 18px;
	font-family: inherit;
	color: #333;
	background-color: white;
	border: 1px solid #D2D2D2;
	border-radius: 8px;
	outline: none;
	resize: none;
	overflow-y: auto;
	white-space: pre-wrap;
	word-wrap: break-word;
	overflow-wrap: break-word;

	&::placeholder {
		color: #979797;
	}

	&:focus,
	&:hover {
		border-color: #3F884F;
	}

	&_disabled,
	&:disabled {
		background-color: #eee;
		cursor: not-allowed;
	}
}
</style>
